/**
 * 指标筛选器组件
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { ReactSortable } from 'react-sortablejs';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { FieldType } from '@dbl/core/dataSource';
import { deepClone } from '@/pages/datalab/utils/utils';
import { useActionHandle } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/useActionHandle';
import { MetricItem } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/components/Metrics/metric_item';
import { getFirstColumn, getFlatOptions } from '@/pages/datalab/pages/BehaviorAnalysis/ExplorationAnalysis/utils';
import CustomMetricItem from '../CustomMetric';
import { IMetricFilterProps } from './types';
import AddButton from '../AddButton';
import './index.scss';

const MetricFilter: React.FC<IMetricFilterProps> = (props) => {
  const { 
    className, 
    title, 
    required,
    value = [],
    eventList = [],
    allMetrics = [],
    optionMetrics = [],
    allFilters = [],
    optionFilters = [],
    optionCustomMetrics = [],
    onChange 
  } = props;
  const { formatMessage } = useIntl();

  // 使用内部状态管理指标列表
  const [columns, setColumns] = useState<IColumnInfo[]>(value || []);

  // 同步外部传入的 value
  useEffect(() => {
    setColumns(value || []);
  }, [value]);

  // 通知父组件状态变化
  const notifyFun = (columnMetrics: IColumnInfo[]) => {
    setColumns(columnMetrics);
    onChange?.(columnMetrics);
  };

  const handle = useActionHandle(columns, notifyFun);
  const { addItem, setItems } = handle;
  
  const selectedItems = useMemo(() => columns.map((item) => item.column_name), [columns]);

  // 所有可选统计指标（不含自定义指标）
  const flatOptions = useMemo(
    () => getFlatOptions(optionMetrics || []),
    [optionMetrics]
  );
  // 所有可选指标已添加完时禁用「添加统计指标」按钮
  const disableAddButton = useMemo(
    () => flatOptions?.length === columns?.filter((c) => !c.is_custom).length,
    [flatOptions, columns]
  );

  // 处理添加指标
  const handleAddMetric = () => {
    if (disableAddButton) {
      return;
    }
    const newColumn = getFirstColumn(allMetrics || [], columns, flatOptions);
    if (!newColumn) {
      return;
    }
    addItem(newColumn);
  };

  // 处理添加自定义指标
  const handleAddCustomMetric = () => {
    // 创建自定义指标的基础结构
    const customMetricIndex = columns.filter((col) => col.is_custom).length + 1;
    const newCustomMetric: IColumnInfo = {
      id: `custom_metric_${Date.now()}`,
      table_id: '',
      table_code: '',
      column_name: `custom_metric_${customMetricIndex}`,
      column_display: `自定义指标${customMetricIndex}`,
      column_display_en: `Custom Metric ${customMetricIndex}`,
      column_type: FieldType.String,
      column_sensitive: false,
      column_granularity: '',
      column_remark: '',
      is_custom: true, // 标识为自定义指标
      is_metric: true,
      metric_method: 0,
      metric_alias: `自定义指标${customMetricIndex}`,
    };
    addItem(newCustomMetric);
  };

  return (
    <div className={classNames('dbl-metric-filter', className)}>
      {title && (
        <div className={classNames('dbl-metric-filter-title', { 'is-required': required })}>
          {title}
        </div>
      )}
      <div className="dbl-metric-filter-content">
        <ReactSortable
          list={columns}
          setList={(list: IColumnInfo[]) => {
            setItems(list);
          }}
          handle=".item-drag"
        >
          {(columns || []).map((column, index) => {
            // 如果是自定义指标，显示自定义指标配置
            if (column.is_custom) {
              return (
                <CustomMetricItem
                  key={`custom_${column.column_name}`}
                  column={column}
                  index={index + 1}
                  handle={handle}
                  eventList={eventList}
                  optionCustomMetrics={optionCustomMetrics ?? []}
                  onUpdate={(updatedColumn) => {
                    const list = [
                      ...columns.slice(0, index),
                      updatedColumn,
                      ...columns.slice(index + 1, columns.length),
                    ];
                    setItems(list);
                  }}
                />
              );
            }
            // 普通指标使用 MetricItem
            return (
              <MetricItem
                fieldSelectClassName="dbl-ab-experiment-field-select"
                key={`${column.table_id}.${column.column_name}`}
                column={column}
                index={index + 1}
                optionMetrics={optionMetrics}
                handle={handle}
                selectedItems={selectedItems}
                handleContent={() => null}
                onSelectItem={(newItemName: string) => {
                  const newColumn = allMetrics.find((item: IColumnInfo) => item.column_name === newItemName);
                  if (!newColumn || newColumn.column_name === column.column_name) {
                    return;
                  }
                  const list = [
                    ...columns.slice(0, index),
                    { ...deepClone(newColumn) },
                    ...columns.slice(index + 1, columns.length),
                  ];
                  setItems(list);
                }}
                allFilters={allFilters}
                optionFilters={optionFilters}
              />
            );
          })}
        </ReactSortable>
        <div className={classNames('dbl-metric-filter-add-buttons', { 'margin-t-10': columns?.length > 0 })}>
          <AddButton
            className="dbl-metric-filter-add-button"
            text={formatMessage({ id: 'dbl_ab_experiment_metric_filter_add_button' })}
            onClick={handleAddMetric}
            disabled={disableAddButton}
          />
          <AddButton
            className="dbl-metric-filter-add-custom-button"
            text={formatMessage({ id: 'dbl_ab_experiment_metric_filter_add_custom_button' })}
            onClick={handleAddCustomMetric}
            disabled={optionCustomMetrics?.length === 0}
          />
        </div>
      </div>
    </div>
  );
};

export default MetricFilter;

